import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { StoreService } from '../../../store/service/store.service';
import { Operator } from '../../model/operator';
import { Sort, Transformations } from '../../model/transformations';
import { DataLoader } from '../../util/data-loader';
import { filterData, sortData } from '../../util/data-util';
import { SortBy } from '../../model/data-source';
import { RequestCriteria } from '../../model/request-criteria';

export enum RemoteDataType {
  REST = 'rest',
  WEBSOCKET = 'websocket'
}

export class MaterialTableDataSource<T> {

  private dataSubject = new BehaviorSubject<T[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(null);
  private dataCountSubject = new BehaviorSubject<number>(0);
  private subscriptions: Subscription[] = [];
  data$ = this.dataSubject.asObservable();
  loading$ = this.loadingSubject.asObservable().pipe(distinctUntilChanged());
  dataCount$ = this.dataCountSubject.asObservable();
  private data?: T[];
  private dataLoader?: DataLoader<T>;

  constructor(private storeService: StoreService, private getTransformations: () => Observable<Transformations>, private remoteDataType: RemoteDataType,
    private sortLocally: boolean = false) { }

  setDataLoader(dataLoader: DataLoader<T>): void {
    this.dataLoader = dataLoader;
    this.subscriptions.push(
      dataLoader.loading$.subscribe(loading => this.loadingSubject.next(loading)),
      dataLoader.dataCount$.subscribe(count => this.dataCountSubject.next(count)),
      dataLoader.data$.subscribe(data => {
        if (this.remoteDataType == RemoteDataType.REST)
          this.data = data;
        if (this.sortLocally) {
          this.getTransformations().subscribe(transformations => {
            if (transformations.sort)
              this.sortDataLocally(transformations.sort);
            else
              this.publishWithColumnFilters(data, transformations.filters);
          });
        } else {
          this.getTransformations().subscribe(transformations => this.publishWithColumnFilters(data, transformations.filters));
        }
      })
    );
  }

  /**
   * Column filters (RequestCriteria.criteria[].columnFilter === true) are excluded from the REST
   * payload (see excludeColumnFilterCriteria) and are a client-side-only quick search over
   * whatever page of results is currently loaded - applied here instead, locally.
   */
  private applyColumnFilters(data: T[], filters?: RequestCriteria): T[] {
    const columnCriteria = filters?.criteria?.filter(c => c.columnFilter) ?? [];
    return columnCriteria.reduce((rows, criteria) => filterData(criteria, rows), data);
  }

  private publishWithColumnFilters(data: T[], filters?: RequestCriteria): void {
    this.dataSubject.next(this.applyColumnFilters(data, filters));
  }

  /** The whole loaded set - store snapshot or loaded page - before column filters and paging cut it. */
  getAllData(): T[] {
    return this.data ?? [];
  }

  subscribeData(dataType: any): void {
    this.loadingSubject.next(true);
    this.subscriptions.push(this.storeService.connectToAllDataUpdates<T>(dataType)
      .subscribe(allData => {
        this.data = allData;
        this.getTransformations().subscribe(transformations => this.transformAndPublishData(transformations));
      }));
  }

  loadData(transformations: Transformations): void {
    this.loadingSubject.next(true);
    if (this.remoteDataType == RemoteDataType.WEBSOCKET)
      this.transformAndPublishData(transformations);
    else if (this.remoteDataType == RemoteDataType.REST) {
      this.loadRestData(transformations);
    }
  }

  sortDataLocally(sort: Sort[]): void {
    this.getTransformations().subscribe(transformations => {
      const sorted = sortData(cloneDeep(this.data), sort);
      const data = this.applyColumnFilters(sorted, transformations.filters);
      this.dataCountSubject.next(data.length);
      this.dataSubject.next(data);
      this.loadingSubject.next(false);
    });
  }

  private transformAndPublishData(transformations: Transformations): void {
    if (this.data) {
      let dataToDisplay: T[] = cloneDeep(this.data);

      if (transformations.sort)
        dataToDisplay = sortData(dataToDisplay, transformations.sort);

      if (transformations.filters?.criteria) {
        transformations.filters.criteria.forEach(criteria => dataToDisplay = filterData(criteria, dataToDisplay));
      }

      this.dataCountSubject.next(dataToDisplay.length);

      const start = transformations.page.pageIndex ? transformations.page.pageIndex * transformations.page.pageSize : 0;
      const end = start + transformations.page.pageSize;
      dataToDisplay = dataToDisplay.slice(start, end);

      this.dataSubject.next(dataToDisplay);
      this.loadingSubject.next(false);
    }
  }

  private loadRestData(transformations: Transformations): void {
    const currentPage = transformations.page.pageIndex;
    const dataPageSize = transformations.page.pageSize;
    const startRow = currentPage * dataPageSize;
    const endRow = startRow + dataPageSize;

    let sorters: SortBy[];
    if (transformations.sort && !this.sortLocally) {
      sorters = transformations.sort.map(v => SortBy.createFromTransformationsSort(v));
    }

    let filters = transformations.filters;
    if (filters == null)
      filters = { operator: Operator.and };
    this.dataLoader.loadTableData(startRow, endRow, dataPageSize, filters, sorters);
  }

  cancel(): void {
    this.dataLoader?.cancel?.();
  }

  disconnect(): void {
    this.dataSubject.complete();
    this.loadingSubject.complete();
    this.dataCountSubject.complete();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }
}
